import axios from "axios";
import { API_BASE_URL } from "../utils/constants";
import { getAuthToken } from "../utils/storage";
import { extractApiError } from "../utils/apiError";
import { applyWarehouseFilter, warehouseParamsSerializer, appendWarehouseParams } from "../utils/warehouseParams";
import { registerClearable } from '../utils/cacheRegistry';

// Axios instance for purchase orders API
const purchaseOrdersAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { "Content-Type": "application/json" },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
purchaseOrdersAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error),
);

// Redirect on auth failure
purchaseOrdersAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    // Skip logout on 401 for bulk-delete endpoints (which use 401 for password errors)
    if (err.response?.status === 401 && !err.config?.url?.includes('/bulk-delete/')) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_data");
      sessionStorage.removeItem("auth_token");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("user_data");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

// v2: purchase order rows now carry `challan_generated`, which gates the edit/delete
// actions. Cached v1 rows lack it and would offer actions the API then refuses, so
// retire them rather than serve them for the rest of the TTL.
const CACHE_PREFIX = "erp_purchase_orders_cache_v2";
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(
      cacheKey(key),
      JSON.stringify({ value, expires: Date.now() + ttl }),
    );
  } catch (e) {}
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try {
    memoryCache.delete(key);
  } catch (e) {}
  try {
    sessionStorage.removeItem(cacheKey(key));
  } catch (e) {}
}

// A cached full list is either a bare array or a paginated `{ items, total }`
// envelope (the newer list endpoints). The modifier used to run only on bare
// arrays, so an envelope kept serving the stale row after an update/delete (P1-4).
function applyListModifier(value, modifierFn) {
  if (Array.isArray(value)) return modifierFn(value.slice());
  if (value && Array.isArray(value.items)) {
    const items = modifierFn(value.items.slice());
    const delta = items.length - value.items.length;
    const total = typeof value.total === 'number' ? Math.max(0, value.total + delta) : value.total;
    return { ...value, items, total };
  }
  return null;
}

function updateFullListCachesWithModifier(modifierFn) {
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === "string" && key.startsWith("all::")) {
        const next = applyListModifier(entry?.value, modifierFn);
        if (next !== null) writeMemoryCache(key, next);
      }
    }
  } catch (e) {}

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const next = applyListModifier(parsed?.value, modifierFn);
          if (next !== null) {
            parsed.value = next;
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) {}
      }
    }
  } catch (e) {}
}

const purchaseOrderService = {
  // 1. post: /api/v1/purchase-orders/existing-supplier
  async createPurchaseOrderExistingSupplier(payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        "/purchase-orders/existing-supplier",
        payload,
      );
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try {
        const key = `byId::${created.id}`;
        writeMemoryCache(key, created);
        writeCache(key, created);
      } catch (e) {}
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 2. post: /api/v1/purchase-orders/new-supplier
  async createPurchaseOrderNewSupplier(payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        "/purchase-orders/new-supplier",
        payload,
      );
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try {
        const key = `byId::${created.id}`;
        writeMemoryCache(key, created);
        writeCache(key, created);
      } catch (e) {}
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createPurchaseOrderExistingSupplierWithAttachments(payload, files = []) {
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      files.forEach(f => fd.append('files', f));
      const response = await purchaseOrdersAPI.post(
        '/purchase-orders/existing-supplier/with-attachments',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) {}
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createPurchaseOrderNewSupplierWithAttachments(payload, files = []) {
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      files.forEach(f => fd.append('files', f));
      const response = await purchaseOrdersAPI.post(
        '/purchase-orders/new-supplier/with-attachments',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) {}
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 3. get: /api/v1/purchase-orders
  async getAllPurchaseOrders(params = {}, options = {}) {
    const { force = false } = options;
    const key = `all::${JSON.stringify(params || {})}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) {
        writeMemoryCache(key, sess);
        return { success: true, data: sess };
      }
    }

    try {
      const queryParams = new URLSearchParams();
      if (params.skip !== undefined) queryParams.append('skip', params.skip);
      else queryParams.append('skip', 0);
      if (params.limit !== undefined) queryParams.append('limit', params.limit);
      else queryParams.append('limit', 20);
      if (params.search) queryParams.append('search', params.search);
      if (params.from_date) queryParams.append('from_date', params.from_date);
      if (params.to_date) queryParams.append('to_date', params.to_date);
      if (params.status) {
        const statuses = Array.isArray(params.status) ? params.status : [params.status];
        statuses.forEach(s => queryParams.append('status', s));
      }
      appendWarehouseParams(queryParams);
      const response = await purchaseOrdersAPI.get(`/purchase-orders?${queryParams.toString()}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 4. get: /api/v1/purchase-orders/{po_id}
  async getPurchaseOrderById(poId, options = {}) {
    const { force = false } = options;
    const key = `byId::${poId}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) {
        writeMemoryCache(key, sess);
        return { success: true, data: sess };
      }

      const fullKey = `all::${JSON.stringify({})}`;
      const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
      if (fullMem && Array.isArray(fullMem)) {
        const found = fullMem.find(
          (i) => String(i.id || i.po_id) === String(poId),
        );
        if (found) {
          writeMemoryCache(key, found);
          writeCache(key, found);
          return { success: true, data: found };
        }
      }
    }

    try {
      const response = await purchaseOrdersAPI.get(`/purchase-orders/${poId}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 5. put: /api/v1/purchase-orders/{po_id}
  async updatePurchaseOrder(poId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-orders/${poId}`,
        payload,
      );
      const updated = response.data;
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? updated : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // `revertStock` decides what happens to the inventory this PO already moved: true
  // gives it back (receipt credit, pending reservation, coils), false removes only the
  // paperwork and leaves every balance standing. The API defaults to reverting, and so
  // does every caller that does not ask the operator.
  async deletePurchaseOrder(poId, { revertStock = true } = {}) {
    try {
      const response = await purchaseOrdersAPI.delete(
        `/purchase-orders/${poId}`,
        { params: { revert_stock: revertStock } },
      );
      updateFullListCachesWithModifier((arr) =>
        arr.filter((i) => String(i.id) !== String(poId)),
      );
      try {
        deleteCacheEntry(`byId::${poId}`);
      } catch (e) {}
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // (Removed deleteAllPurchaseOrders -> /purchase-orders/delete-all and
  //  bulkDeletePurchaseOrders -> /purchase-orders/bulk-delete: neither route exists on
  //  the backend and neither had any caller — dead code that invited someone to wire up
  //  a second, unguarded bulk-delete path. Purchase bulk delete goes through
  //  ordersService.bulkDeleteSelectedOrders / bulkDeletePurchaseOrdersGlobal.)

  // 7. post: /api/v1/purchase-orders/{po_id}/confirm
  async confirmPurchaseOrder(poId) {
    try {
      // Call the confirm endpoint
      await purchaseOrdersAPI.post(`/purchase-orders/${poId}/confirm`);

      // Refetch the updated purchase order since the API doesn't return it
      const fetchResponse = await purchaseOrdersAPI.get(
        `/purchase-orders/${poId}`,
      );
      const updated = fetchResponse.data;

      // Update caches with the fresh data
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? updated : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 8. post: /api/v1/purchase-orders/{po_id}/cancel
  async cancelPurchaseOrder(poId) {
    try {
      // Call the cancel endpoint
      await purchaseOrdersAPI.post(`/purchase-orders/${poId}/cancel`);

      // Refetch the updated purchase order since the API doesn't return it
      const fetchResponse = await purchaseOrdersAPI.get(
        `/purchase-orders/${poId}`,
      );
      const updated = fetchResponse.data;

      // Update caches with the fresh data
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? updated : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 9. get: /api/v1/purchase-advices
  async getAllPurchaseAdvices(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get("/purchase-advices", {
        params,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 10. get: /api/v1/purchase-advices/{po_id}
  async getPurchaseAdviceById(poId) {
    try {
      const response = await purchaseOrdersAPI.get(`/purchase-advices/${poId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // post: /api/v1/purchase-advices/{advice_id}/save-unloading-draft
  async saveUnloadingDraft(adviceId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-advices/${adviceId}/save-unloading-draft`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 11. put: /api/v1/purchase-advices/{po_id}
  async updatePurchaseAdvice(poId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-advices/${poId}`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 11b. delete: /api/v1/purchase-advices/{po_id}
  // NOTE: no UI calls this yet. Every other order/PO delete in the app goes through
  // a confirmation dialog before it fires — wire one before giving this a button.
  async deletePurchaseAdvice(poId) {
    try {
      const response = await purchaseOrdersAPI.delete(
        `/purchase-advices/${poId}`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 12. post: /api/v1/purchase-advices/{po_id}/generate
  async generatePurchaseAdvice(poId, payload = {}) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-orders/${poId}/advices`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 12b. post: /api/v1/purchase-advices/{advice_id}/assign-supervisor
  async assignSupervisorToAdvice(adviceId, payload = {}) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/assign-supervisor`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 14b. post: /api/v1/purchase-advices/{advice_id}/start-unloading
  async startUnloadingPurchaseAdvice(adviceId, payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/start-unloading`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 14c. post: /api/v1/purchase-advices/{advice_id}/complete-unloading
  async completeUnloadingPurchaseAdvice(adviceId, payload) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/complete-unloading`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 15. get: /api/v1/purchase-challans
  async getAllPurchaseChallans(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get("/purchase-challans", {
        params,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 16. get: /api/v1/purchase-challans/{po_id}
  async getPurchaseChallanById(poId) {
    try {
      const response = await purchaseOrdersAPI.get(
        `/purchase-challans/${poId}`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // PUT /purchase-advices/{advice_id}/edit-challan
  async editPurchaseChallan(adviceId, payload) {
    try {
      const response = await purchaseOrdersAPI.put(
        `/purchase-advices/${adviceId}/edit-challan`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 18b. post: /api/v1/purchase-advices/{advice_id}/generate-challan
  async generateAdviceChallan(adviceId, payload = {}) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/generate-challan`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // post: /api/v1/purchase-advices/{advice_id}/mark-challan-printed
  async markAdviceChallanPrinted(adviceId) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-advices/${adviceId}/mark-challan-printed`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 19b. post: /api/v1/purchase-orders/{po_id}/mark-all-advised
  async markAllAdvised(poId) {
    try {
      const response = await purchaseOrdersAPI.post(
        `/purchase-orders/${poId}/mark-all-advised`,
      );
      const updated = response.data;
      updateFullListCachesWithModifier((arr) =>
        arr.map((i) =>
          String(i.id || i.po_id) === String(poId) ? { ...i, ...updated } : i,
        ),
      );
      try {
        const key = `byId::${poId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) {}
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 22. delete: /api/v1/purchase-daybook/{advice_id}
  // A daybook row IS a purchase advice: pass the row's id (an ADVICE id). The backend
  // resolves it to the parent PO and deletes that PO with all its advices.
  // NOTE: no UI calls this yet, and it defaults to revertStock=true — the opposite
  // of the bulk-delete default. Confirm both the dialog and the default before use.
  async deletePurchaseDaybookEntry(adviceId, { revertStock = true } = {}) {
    try {
      const response = await purchaseOrdersAPI.delete(
        `/purchase-daybook/${adviceId}`,
        { params: { revert_stock: revertStock } },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 23. get: /api/v1/reports/pending-purchase-advice
  async getPendingPurchaseAdviceReport(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get(
        "/reports/pending-purchase-advice",
        { params },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 24. get: /api/v1/reports/purchase-estimate
  async getPurchaseEstimateReport(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get(
        "/reports/purchase-estimate",
        { params },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // 25. get: /api/v1/purchase-daybook
  async getPurchaseDaybook(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get("/purchase-daybook", {
        params,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async exportPurchaseChallans(params = {}) {
    try {
      const response = await purchaseOrdersAPI.get('/purchase-challans/export/download', {
        params,
        responseType: 'blob',
      });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async exportPurchaseDaybook(params = {}) {
    try {
      const query = new URLSearchParams({
        ...params,
        format: params.format || 'csv'
      });
      appendWarehouseParams(query);
      const response = await purchaseOrdersAPI.get('/purchase-challans/export/download', {
        params: Object.fromEntries(query.entries()),
        responseType: 'blob',
      });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  clearCache() {
    try {
      // Memory keys are "all::…" / "byId::…" — the old substring match never hit, so
      // the in-memory list outlived clearCache() and kept serving stale rows (P1-4).
      memoryCache.clear();
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) {}
  },

  async sendNotification(id, type = 'email') {
    try {
      const response = await purchaseOrdersAPI.post(`/purchase-orders/${id}/send-notification`, null, { params: { type } });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },
};

// Cleared on logout together with the other services (policy C16).
registerClearable(() => purchaseOrderService.clearCache());

export default purchaseOrderService;
